import { useCallback, useMemo, useRef, useState } from 'react'
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react'

import {
  EMPTY_OBJECT,
  EMPTY_STRING,
  FLOATING_ELEMENT_VIEWPORT_PADDING,
  SYMBOL_HYPHEN,
} from '../../constants/common'
import {
  CLOSE_ON_ANCESTOR_SCROLL_USER,
  SELECT_DROPDOWN_MIN_HEIGHT,
  SELECT_DROPDOWN_ROLE,
  SELECT_OFFSET_MAIN_AXIS,
} from '../../constants/select'
import { toCSSPixel } from '../../utils/styles'
import { useSelectGlobalClose } from '../useSelectGlobalClose'
import { useUserAncestorScroll } from '../useUserAncestorScroll'

import {
  getAvailableSideHeight,
  isCloseOnAncestorScrollAlways,
  isCloseOnAncestorScrollUser,
} from './useFloatingSelect.utils'

/**
 * @param {string} [closeOnAncestorScroll] When to close on ancestor scroll (`user` / `always`).
 * @param {object} [containerRef] Boundary element ref for the `shift` middleware.
 * @param {boolean} [disabled=false] Turns off the list keyboard navigation, so
 *   arrow keys on the reference do not open a disabled select.
 * @param {number} [dropdownMinHeight] Dropdown min height, px (from `dropdownStyles.minHeight`).
 * @param {object} [dropdownStyles] Extra size constraints (`maxHeight` / `maxWidth`).
 * @param {object} [flipConfig] Options forwarded to floating-ui `flip` (e.g. `fallbackPlacements`).
 * @param {number} [initialActiveIndex=0] Index of the list item focused on open.
 * @param {boolean} [maxWidthByRef] Match the dropdown width to the reference width.
 * @param {number} [offsetMainAxis] Main-axis offset between reference and dropdown, px.
 * @param {object} [offsetConfig] Extra options forwarded to the `offset` middleware.
 * @param {string} placement Preferred floating placement (e.g. `bottom-start`).
 * @param {boolean} [shouldClampHeight=true] Clamps the dropdown height to the
 *   available space (with internal scroll) — suitable for long lists.
 *   Set to `false` for fixed-size content (e.g. a color picker) so it keeps
 *   full height and `flip` can move it to a fitting placement (top/side)
 *   instead of being shrunk.
 * @param {number} [viewportPadding=FLOATING_ELEMENT_VIEWPORT_PADDING] Minimum gap (px) kept
 *   between the floating element and the viewport edge by `shift`, `flip` and `size`.
 * @param {Function} [onClose] Called when the dropdown closes.
 * @param {Function} [onOpenChange] Custom open-state change handler (overrides the default).
 */
export function useFloatingSelect({
  closeOnAncestorScroll = CLOSE_ON_ANCESTOR_SCROLL_USER,
  containerRef,
  disabled = false,
  dropdownStyles: {
    minHeight: dropdownMinHeight = SELECT_DROPDOWN_MIN_HEIGHT, // px
    ...dropdownStyles
  } = EMPTY_OBJECT,
  flipConfig = EMPTY_OBJECT,
  initialActiveIndex = 0,
  maxWidthByRef,
  offsetConfig: {
    mainAxis: offsetMainAxis = SELECT_OFFSET_MAIN_AXIS,
    ...offsetConfig
  } = EMPTY_OBJECT,
  placement,
  shouldClampHeight = true,
  viewportPadding = FLOATING_ELEMENT_VIEWPORT_PADDING,
  onClose,
  onOpenChange,
} = EMPTY_OBJECT) {
  const [isOpen, setOpen] = useState(false)

  const [activeIndex, setActiveIndex] = useState(null)

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)

  const elementsRef = useRef([]) // Do not change to `EMPTY_LIST` cause it will change constant

  useSelectGlobalClose({ setOpen })

  // Set first list item focused when the dropdown opens, during render.
  // Did not find better way to do it via `floating`.
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen)

    if (isOpen) {
      setActiveIndex(initialActiveIndex)
    }
  }

  const handleChangeOpen = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- hook intentionally bails on floating-ui ref read; manual memo kept to avoid re-render churn
    nextOpen => {
      if (!nextOpen) {
        onClose?.()
      }

      setOpen(nextOpen)
    },
    [onClose]
  )

  // https://floating-ui.com/docs/middleware
  // Order of the objects in middleware matters
  const middleware = [
    offset({ ...offsetConfig, mainAxis: offsetMainAxis }),
    size({
      apply({ availableHeight, availableWidth, elements, rects }) {
        if (shouldClampHeight) {
          // Measured before the style writes below — a write-then-read forces a sync reflow
          const availableSideHeight = getAvailableSideHeight({
            availableHeight,
            gap: offsetMainAxis + viewportPadding,
            referenceElement: elements.reference,
          })

          // Add min height to prevent dropdown opening in wrong placement
          elements.floating.style.minHeight = toCSSPixel(dropdownMinHeight)

          const maxHeight = toCSSPixel(
            Math.min(dropdownStyles?.maxHeight || Infinity, availableSideHeight)
          )

          // Adding passing variable to correct work any container inside of `floating` container
          elements.floating.style.setProperty(
            '--floating-select-dropdown-list-max-height',
            maxHeight
          )

          // Apply `maxHeight` to `floating` container
          elements.floating.style.maxHeight = maxHeight
        } else {
          // Clear any height clamp previously applied so toggling the flag back
          // to `false` doesn't leave the element stuck at a clamped size.
          elements.floating.style.minHeight = EMPTY_STRING
          elements.floating.style.maxHeight = EMPTY_STRING
          elements.floating.style.removeProperty(
            '--floating-select-dropdown-list-max-height'
          )
        }

        if (maxWidthByRef) {
          elements.floating.style.width = toCSSPixel(rects.reference.width)
        } else if (dropdownStyles?.maxWidth) {
          const maxWidth = toCSSPixel(
            dropdownStyles?.maxWidth < availableWidth
              ? dropdownStyles?.maxWidth
              : availableWidth
          )

          elements.floating.style.setProperty(
            '--floating-select-dropdown-list-max-width',
            maxWidth
          )
          elements.floating.style.maxWidth = maxWidth
        }
      },
      padding: viewportPadding,
    }),
  ]

  const flipMiddleware = flip({ ...flipConfig, padding: viewportPadding })
  // floating-ui consumes the middleware array as opaque values, so it cannot
  // tell that reading `containerRef.current` here is harmless. Re-reading on
  // every render keeps the shift boundary in sync as soon as the node mounts.
  const shiftMiddleware = shift({
    // eslint-disable-next-line react-hooks/refs
    ...(containerRef ? { boundary: containerRef?.current } : null),
    padding: viewportPadding,
  })

  // https://floating-ui.com/docs/flip#combining-with-shift
  if (placement.includes(SYMBOL_HYPHEN)) {
    middleware.push(flipMiddleware, shiftMiddleware)
  } else {
    middleware.push(shiftMiddleware, flipMiddleware)
  }

  const { context, floatingStyles, refs } = useFloating({
    middleware,
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
    onOpenChange: onOpenChange || handleChangeOpen,
  })

  useUserAncestorScroll({
    closeOnUserAncestorScroll: isCloseOnAncestorScrollUser(
      closeOnAncestorScroll
    ),
    context,
    onOpenChange: onOpenChange || handleChangeOpen,
  })

  const dismiss = useDismiss(context, {
    ancestorScroll: isCloseOnAncestorScrollAlways(closeOnAncestorScroll),
  })

  const listNav = useListNavigation(context, {
    activeIndex,
    enabled: !disabled,
    listRef: elementsRef,
    virtual: true,
    onNavigate: setActiveIndex,
  })
  const role = useRole(context, { role: SELECT_DROPDOWN_ROLE })

  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions(
    [role, dismiss, listNav]
  )

  const floatingContext = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- hook intentionally bails on floating-ui ref read; manual memo kept to avoid re-render churn
    () => ({
      ...context,
      setOpen,
    }),
    [context]
  )

  return {
    activeIndex,
    context: floatingContext,
    elementsRef,
    floatingStyles,
    getFloatingItemProps: getItemProps,
    getFloatingProps,
    getReferenceProps,
    isOpen,
    refs,
    setActiveIndex,
    setOpen,
  }
}
